import { useMembersContext } from "@pages/members/useMembers";
import { CloseIcon, KpiDataMap, PrimaryButton, ReminderTimeZone, ViewButtons, getCalFormattedDate, getDiffFromCurrent, isInCurrentMonth, isInCurrentWeek } from "./subComps";
import { useRemindersContext } from "./useReminders";
import { useMemo, useState } from "react";
import moment from "moment-timezone";
import { DAY, MONTH, WEEK } from "./constant";
import { Col, DatePicker, Row } from "antd";
import { ADD, SUBTRACT, dateFormatConfig } from "@utils/constants";
import { getClientName, milleniumClientCheck } from "@utils/helpers";
import { CalendarMonthViewSwitch } from "@components/drawerOutreaches/Cadence";

interface ReminderHeadingProps {
  closeDrawer: () => void;
}

export function ReminderHeader({ closeDrawer }: ReminderHeadingProps) {
  const { KPIreminderData: KpiData, viewType } = useRemindersContext();

  const { sidebarExpand } = useMembersContext();

  return (
    <>
      <div className="reminder-drawer__header">
        <div className="reminder-drawer__header__title">Reminders</div>
        <div className={`reminder-drawer__header__reminder-content ${sidebarExpand ? "expanded" : ""}`}>
          <KpiDataMap KpiData={KpiData} />
          {sidebarExpand && <GlobalReminderDatePicker />}
        </div>
        {
          viewType === MONTH && milleniumClientCheck() &&
          <CalendarMonthViewSwitch />
        }
        <ViewButtons />
        <CloseIcon closeDrawer={closeDrawer} />
      </div>
      {!sidebarExpand && <GlobalReminderDatePicker />}
    </>
  );
}

export function GlobalReminderDatePicker() {
  const { viewType } = useRemindersContext();

  return (
    <div className="reminder-drawer__header__reminder-date_picker">
      {viewType === DAY && <DayViewDatePicker />}
      {viewType === WEEK && <WeekViewDatePicker />}
      {viewType === MONTH && <MonthViewDatePicker />}
    </div>
  );
}

function DayViewDatePicker() {
  const { selectDate, setSelectDate, filteredDates, getReminderDates, fetchRemindersBasedOnView} = useRemindersContext();
  const { sidebarExpand } = useMembersContext();

  const [datePickerOpen, setdatePickerOpen] = useState<boolean>(false);

  const changeSelectedDate = ({action , value} : {action : string , value ?: any}) => {
    let copyDate;
    if(!value) {
      copyDate = action === ADD ? moment(selectDate).add(1, "d") : moment(selectDate).subtract(1, "d");
    } else {
      copyDate = value;
    }
    
    // setSelectedDate(copyDate);
    setSelectDate(copyDate);
    fetchRemindersBasedOnView({dateRange : [copyDate, copyDate]});
  };

  const { isToday } = useMemo(() => {
    const differenceFromCurrentDate = getDiffFromCurrent(selectDate);

    const isToday = differenceFromCurrentDate === 0;

    return { isToday };
  }, [selectDate]);

  return (
    <>
      {datePickerOpen && <div className="overlay-modal overlay-modal--left" onClick={() => setdatePickerOpen(false)}></div>}
      <Row>
        <Col span={24} className="calender-action-panel">
          <div className="calender-panel">
            <span className="prev">
              <i className="icon icon-chevron-left" onClick={() => changeSelectedDate({action : SUBTRACT})}></i>
            </span>
            {/* {isToday && <span className="w-500 f16 clr-p100 today">Today</span>} */}
            <DatePicker
              bordered={false}
              value={selectDate}
              onOpenChange={(open: boolean) => {
                if (open) {
                  getReminderDates();
                }
                setdatePickerOpen(open);
              }}
              onChange={(date: any, val: any) => {
                // setSelectDate(moment(val));
                fetchRemindersBasedOnView({dateRange : [moment(val)?.format("YYYY-MM-DD"), moment(val)?.format("YYYY-MM-DD")]});
                setSelectDate(moment(val));
                setdatePickerOpen(false);
              }}
              format={dateFormatConfig.get(getClientName())["defaultDateFormat"]}
              dropdownClassName="ui-select-date-picker"
              defaultValue={moment()}
              dateRender={(current) => getCalFormattedDate(current, filteredDates)}
              onPanelChange={(value) => getReminderDates(value)}
            />
            <span className="next">
              <i className="icon icon-chevron-right" onClick={() => changeSelectedDate({action : ADD})}></i>
            </span>
            <PrimaryButton isActive={isToday} clickHandler={() => changeSelectedDate({action : '' , value : moment()})} label={'Today'}/>
            {!sidebarExpand && <ReminderTimeZone />}
          </div>
        </Col>
      </Row>
    </>
  );
}

function WeekViewDatePicker() {
  const { selectDate, setSelectDate, fetchRemindersBasedOnView, getReminderDates, filteredDates } = useRemindersContext();
  const { sidebarExpand } = useMembersContext();
  const [showDatePicker, setShowDatePicker] = useState(false);

  const toggleDatePicker = (flag: any = false) => {
    setShowDatePicker(flag);
  };

  const { startOfWeek, endOfWeek, isCurrentWeek } = useMemo(() => {
    const startOfWeek = selectDate.clone().startOf("week");
    const endOfWeek = selectDate.clone().endOf("week");

    const isCurrentWeek = isInCurrentWeek(selectDate);
    return { startOfWeek, endOfWeek, isCurrentWeek };
  }, [selectDate]);

  function changeWeek({action , value} : {action : string , value ?: any}){
    let copyDate;
    if(!value) {
      copyDate = action === ADD ? moment(selectDate).add(1, "week") : moment(selectDate).subtract(1, "week");
    } else {
      copyDate = value;
    }
    getWeekReminderData(copyDate);
  }

  const getWeekReminderData = (date: any) => {
    setSelectDate(date);
    fetchRemindersBasedOnView({ dateRange: [date.clone().startOf("week"), date.clone().endOf("week")] });
  };

  const currentMonthSelector = () => {
    return (
      <div className="calender-panel__date-picker__footer">
        <div className="calender-panel__date-picker__footer__button" onClick={() => {getWeekReminderData(moment());toggleDatePicker()}}>
          Current Week
        </div>
      </div>
    );
  };

  return (
    <>
    {showDatePicker && <div className="overlay-modal overlay-modal--left" onClick={() => toggleDatePicker(false)}></div>}
    <Row>
      <Col span={24} className="calender-action-panel">
        <div className="calender-panel">
          <span className="prev">
            <i
              className="icon icon-chevron-left"
              onClick={() => {
                changeWeek({action : SUBTRACT});
                toggleDatePicker();
              }}
            ></i>
          </span>
          <span
            onClick={() => {
              if (!showDatePicker) {
                getReminderDates();
              }
            }}
          >
            {/* {isCurrentWeek && <span className="w-500 f16 clr-p100 today">This Week</span>} */}
            <span
              className="calender-panel__date"
              onClick={() => {
                setShowDatePicker((prev) => !prev);
              }}
            >
              {`${startOfWeek.format("MM-DD-YYYY")}`}
              <span className="w-400"> to </span>
              {`${endOfWeek.format("MM-DD-YYYY")}`}
            </span>
            <span className="calender-panel__date-picker">
              <DatePicker
                dropdownClassName="ui-select-date-picker ui-select-date-picker__week-view"
                open={showDatePicker}
                onChange={(date: any, dateString: any) => {
                  getWeekReminderData(moment(date));
                  toggleDatePicker();
                }}
                dateRender={(current) => getCalFormattedDate(current, filteredDates)}
                onPanelChange={(value) => getReminderDates(value)}
                renderExtraFooter={currentMonthSelector}
                value={selectDate}
              />
            </span>
          </span>
          <span className="next">
            <i
              className="icon icon-chevron-right"
              onClick={() => {
                changeWeek({action : ADD});
                toggleDatePicker();
              }}
            ></i>
          </span>
          <PrimaryButton isActive={isCurrentWeek} clickHandler={() => getWeekReminderData(moment())} label={'This Week'}/>
          {!sidebarExpand && <ReminderTimeZone />}
        </div>
      </Col>
    </Row>
    </>
  );
}

function MonthViewDatePicker() {
  const { selectDate, setSelectDate, fetchRemindersBasedOnView, getReminderDates } = useRemindersContext();
  const { sidebarExpand } = useMembersContext();

  const [showDatePicker, setShowDatePicker] = useState(false);

  const toggleDatePicker = (flag: any = false) => {
    setShowDatePicker(flag);
  };

  function changeMonth(add: boolean) {
    let copyDate = add ? moment(selectDate).add(1, "month") : moment(selectDate).subtract(1, "month");
    getMonthReminderData(copyDate);
  }

  const getMonthReminderData = (date: any) => {
    setSelectDate(date);
    fetchRemindersBasedOnView({ dateRange: [date.clone().startOf("month"), date.clone().endOf("month")] });
  };

  const { startOfMonth, endOfMonth, isCurrentMonth } = useMemo(() => {
    const startOfMonth = selectDate.clone().startOf("month");
    const endOfMonth = selectDate.clone().endOf("month");

    const isCurrentMonth = isInCurrentMonth(selectDate);

    return { startOfMonth, endOfMonth, isCurrentMonth };
  }, [selectDate]);

  const currentMonthSelector = () => {
    return (
      <div className="calender-panel__date-picker__footer">
        <div className="calender-panel__date-picker__footer__button" onClick={() => {getMonthReminderData(moment().startOf("month"));toggleDatePicker(false);}}>
          Current Month
        </div>
      </div>
    );
  };

  return (
    <>
    {showDatePicker && <div className="overlay-modal overlay-modal--left" onClick={() => toggleDatePicker(false)}></div>}
    <Row>
      <Col span={24} className="calender-action-panel">
        <div className="calender-panel">
          <span className="prev">
            <i
              className="icon icon-chevron-left"
              onClick={() => {
                changeMonth(false);
                toggleDatePicker();
              }}
            ></i>
          </span>
          <span
            onClick={() => {
              if (!showDatePicker) {
                getReminderDates();
              }
            }}
          >
            {/* {isCurrentMonth && <span className="w-500 f16 clr-p100 today">This Month</span>} */}
            <span
              className="calender-panel__date"
              onClick={() => {
                setShowDatePicker((prev) => !prev);
              }}
            >
              {`${startOfMonth.format("MM-DD-YYYY")}`}
              <span className="w-400"> to </span>
              {`${endOfMonth.format("MM-DD-YYYY")}`}
            </span>
            <span className="calender-panel__date-picker">
              <DatePicker
                value={selectDate}
                picker="month"
                open={showDatePicker}
                onChange={(date: any, dateString: any) => {
                  getMonthReminderData(moment(date).startOf("month"));
                  toggleDatePicker(false);
                }}
                renderExtraFooter={currentMonthSelector}
              />
            </span>
          </span>

          <span className="next">
            <i
              className="icon icon-chevron-right"
              onClick={() => {
                changeMonth(true);
                toggleDatePicker();
              }}
            ></i>
          </span>
          <PrimaryButton isActive={isCurrentMonth} clickHandler={() => getMonthReminderData(moment().startOf("month"))} label={'This Month'}/>
          {!sidebarExpand && <ReminderTimeZone />}
        </div>
      </Col>
    </Row>
    </>
  );
}
